import React, { useState } from "react";
import {Breadcrumb, Layout, theme, Tabs} from "antd";
import {useAuth} from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { UserOutlined, BellOutlined, MailOutlined, SafetyOutlined } from "@ant-design/icons";
import { Utils } from "../utils/utils";
import MyProfile from "../components/Profile/MyProfile";
import Notifications from "../components/Profile/Notifications";
import Messages from "../components/Profile/Messages";
import CookieSettings from "../components/Profile/CookieSettings";
import { useGetSignupConfigQuery } from "../services/nestjs-base-api";

const { Content } = Layout;

const ProfilePage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Check if cookie consent feature is enabled
    const { data: config } = useGetSignupConfigQuery();

    // Check URL for active tab - default to profile, but support notifications/messages
    const getActiveTab = () => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab') || 'profile';
        // For org admins, convert 'notifications' to 'messages' for compatibility
        if (Utils.userIsOrganisationAdmin(user) && tab === 'notifications') {
            return 'messages';
        }
        return tab;
    };

    const [activeTab, setActiveTab] = useState(getActiveTab());

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        // Update URL to reflect the active tab
        const searchParams = new URLSearchParams(location.search);
        if (key === 'profile') {
            searchParams.delete('tab'); // Remove tab param for default
        } else {
            searchParams.set('tab', key);
        }
        const newSearch = searchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    };

    const isAgencyUser = Utils.userIsAgencyUser(user);

    const tabItems = [
        {
            key: 'profile',
            label: (
                <span>
                    <UserOutlined style={{ marginRight: '8px' }} />
                    My Profile
                </span>
            ),
            children: <MyProfile />
        },
        // Show Messages tab for org admins and agency users, Notifications for regular users
        ...(Utils.userIsOrganisationAdmin(user) || isAgencyUser ? [{
            key: 'messages',
            label: (
                <span>
                    <MailOutlined style={{ marginRight: '8px' }} />
                    Messages
                </span>
            ),
            children: <Messages />
        }] : [{
            key: 'notifications',
            label: (
                <span>
                    <BellOutlined style={{ marginRight: '8px' }} />
                    Notifications
                </span>
            ),
            children: <Notifications />
        }]),
        // Only show Privacy tab if cookie consent feature is enabled
        ...(config?.cookieConsentEnabled ? [{
            key: 'privacy',
            label: (
                <span>
                    <SafetyOutlined style={{ marginRight: '8px' }} />
                    Privacy
                </span>
            ),
            children: <CookieSettings />
        }] : [])
    ];

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user.organisation ? user.organisation.name : intl.formatMessage({ id: 'common.platformName' }) },
                    { title: intl.formatMessage({ id: 'section.dashboardName' }) },
                    { title: intl.formatMessage({ id: 'breadcrumb.profile' }) }
                ]}
            />
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={tabItems}
                    size="large"
                    style={{ marginTop: '-8px' }}
                />
            </div>
        </Content>
    );
}

export default ProfilePage;
